import React from 'react';
import { IconName, Scale, useTheme } from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { getFormattedPriceWithBookingFee, getTabPriceLabelling } from '@utils/pricing.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import { useServerState } from '@utils/useServerState';
import { useTranslations } from '@utils/useTranslations';
import { FlightsSelectOption } from '@features/Flights';
import { LoadingSkeletonSection } from '../LoadingSkeleton/LoadingSkeleton';
import { TabSkeleton, Tabs, TabsSkeleton } from './FlightSelectTabs.styles';
import { Tab } from './Tab';

interface FlightsSelectTabsProps {
  onClick: (trip: FlightsSelectOption) => void;
  tripType: FlightsSelectOption;
  isAtolProtected: boolean;
}

const FlightsSelectTabs = ({ onClick, tripType, isAtolProtected }: FlightsSelectTabsProps) => {
  const { sale } = useServerState();
  const { pricingRules } = useAppState();
  const { translations } = useTranslations();
  const { currency, isMobile, territoryLocale } = useApplicationState();
  const { prices } = sale ?? {};

  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);

  const hotelOnlyPriceValueInfo = {
    leadRate: bookingFeesEnabled
      ? getFormattedPriceWithBookingFee(prices?.leadRate?.unit ?? 0, currency, territoryLocale)
      : (prices?.leadRate?.forDisplay ?? ''),
    rackRate: prices?.rackRate?.forDisplay ?? '',
    showRackRate: !!pricingRules.showRackRate,
  };

  const hotelOnlyTranslationData = getTabPriceLabelling({
    pricing: prices,
    flightsSelected: false,
    translations,
  });

  const hotelPlusPriceValueInfo = {
    leadRate: prices?.withFlights?.forDisplay ?? '',
    rackRate: '',
    showRackRate: false,
  };

  const hotelPlusTranslationData = getTabPriceLabelling({
    pricing: prices,
    flightsSelected: true,
    translations,
  });

  return (
    <Tabs>
      <Tab
        isSelected={tripType === FlightsSelectOption.hotelOnly}
        onClick={() => onClick(FlightsSelectOption.hotelOnly)}
        title={i18n('sale.hotel.only', [], translations)}
        showAtolProtected={false}
        priceValueInfo={hotelOnlyPriceValueInfo}
        translationData={hotelOnlyTranslationData}
        iconName={IconName.hotel}
        borderRadiusPosition={isMobile ? 'right' : 'left'}
      />
      <Tab
        isSelected={tripType === FlightsSelectOption.hotelWithFlights}
        onClick={() => onClick(FlightsSelectOption.hotelWithFlights)}
        title={i18n('sale.hotel.plus.flights', [], translations)}
        showAtolProtected={isAtolProtected}
        priceValueInfo={hotelPlusPriceValueInfo}
        translationData={hotelPlusTranslationData}
        iconName={IconName.flightDepart}
        borderRadiusPosition={isMobile ? 'left' : 'right'}
      />
    </Tabs>
  );
};

const Skeleton = () => {
  const { color } = useTheme();

  const commonLoadingStyle = {
    height: Scale.m,
    borderRadius: Scale.xxs,
    color: color?.grey05,
  };

  return (
    <TabsSkeleton data-testid="tab-skeleton">
      <TabSkeleton borderRadiusPosition="left">
        <LoadingSkeletonSection {...commonLoadingStyle} width="50%" />
        <LoadingSkeletonSection {...commonLoadingStyle} />
      </TabSkeleton>
      <TabSkeleton borderRadiusPosition="right">
        <LoadingSkeletonSection {...commonLoadingStyle} width="50%" />
        <LoadingSkeletonSection {...commonLoadingStyle} />
      </TabSkeleton>
    </TabsSkeleton>
  );
};

FlightsSelectTabs.Skeleton = Skeleton;

export default FlightsSelectTabs;
